import React, { useState, useEffect } from 'react';

const GameBoard = ({
  grid = [],
  isPlayerGrid = false,
  onCellClick = null,
  activeAbility = null,
  annihilateVertical = false,
  hackerResult = null,
  playerData = {}, // Default to empty object to avoid errors
  gridSize = 8, // Support dynamic grid size
}) => {
  // Ensure we have a proper grid with dynamic size
  const normalizedGrid = Array.isArray(grid) && grid.length === gridSize ? grid :
    Array(gridSize).fill().map(() =>
      Array(gridSize).fill().map(() => ({ ship: null, hit: false, miss: false }))
    );

  const [hoverCoords, setHoverCoords] = useState(null);
  const [lastAttackedCell, setLastAttackedCell] = useState(null);

  // Reset last attacked cell after animation completes
  useEffect(() => {
    if (lastAttackedCell) {
      const timer = setTimeout(() => {
        setLastAttackedCell(null);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [lastAttackedCell]);
  const isAffectedByAbility = (x, y) => {
    if (!activeAbility || !hoverCoords) return false;

    switch (activeAbility) {
      case 'NUKE':
        // X pattern for nuke
        return (x === hoverCoords.x && y === hoverCoords.y) || // center
          (Math.abs(x - hoverCoords.x) === 1 && Math.abs(y - hoverCoords.y) === 1); // diagonals
      case 'SCANNER':
        // 2x2 area for scanner
        return x >= hoverCoords.x && x < hoverCoords.x + 2 &&
          y >= hoverCoords.y && y < hoverCoords.y + 2;
      case 'ANNIHILATE':
        if (annihilateVertical) {
          // Vertical pattern (3 consecutive cells)
          return x === hoverCoords.x &&
            y >= hoverCoords.y &&
            y <= hoverCoords.y + 2 &&
            hoverCoords.y + 2 < gridSize;
        } else {
          // Horizontal pattern (3 consecutive cells)
          return y === hoverCoords.y &&
            x >= hoverCoords.x - 1 &&
            x <= hoverCoords.x + 1 &&
            hoverCoords.x > 0 &&
            hoverCoords.x < gridSize - 1;
        }
      case 'HACKER':
        // No grid targeting (reveals random ship)
        return false;
      case 'COUNTER':
        // No grid targeting (defensive installation)
        return false;
      case 'JAM':
        // No grid targeting (defensive installation)
        return false;

      default:
        // Single cell for other abilities
        return x === hoverCoords.x && y === hoverCoords.y;
    }
  };

  const renderCell = (cell = {}, x, y) => {
    const colLabel = String.fromCharCode(65 + x); // A-H
    const rowLabel = gridSize - y; // 8 down to 1
    const cellLabel = `${colLabel}${rowLabel}`;

    // Check if this cell is revealed by the hacker ability
    const isHackerRevealed = hackerResult && hackerResult.col === x && hackerResult.row === y;


    let cellClass = "w-full h-full border border-gray-600 rounded-sm flex items-center justify-center transition-all duration-200 relative group ";

    // Add animations for recently attacked cells
    if (lastAttackedCell && lastAttackedCell.x === x && lastAttackedCell.y === y) {
      cellClass += "animate-pulse scale-105 shadow-lg shadow-white/30 ";
    }

    // Check for hacker revealed cells (enemy ship revealed with faint green glow)
    if (!isPlayerGrid && isHackerRevealed && cell.ship) {
      cellClass += "bg-green-500/20 border-green-500/40 shadow-inner shadow-green-500/30 ";
    }

    if (cell.hit === true) {
      cellClass += "bg-red-500 animate-hit";

      // Add special styling if the hit was from a counter-attack
      if (cell.fromCounter) {
        cellClass += " border-2 border-blue-400 shadow-md shadow-blue-500/50 ";
      }
    } else if (cell.miss === true) {
      cellClass += "bg-blue-500 animate-miss";
    } else if (isPlayerGrid && cell.ship) {
      cellClass += "bg-green-500 border-green-700"; // Show your ships in green
    } else if (isAffectedByAbility(x, y)) {
      // Visualize different abilities
      switch (activeAbility) {
        case 'NUKE':
          cellClass += "bg-orange-500/60 animate-pulse-danger";
          break;
        case 'ANNIHILATE':
          cellClass += "bg-red-500/60 animate-pulse-danger";
          break;
        case 'SCANNER':
          cellClass += "bg-yellow-500/60 animate-pulse-info";
          break;
        case 'HACKER':
          cellClass += "bg-green-500/60 animate-pulse-info";
          break;
        default:
          cellClass += "bg-purple-500/60 animate-pulse";
      }
    } else if ((!isPlayerGrid && activeAbility)) {
      cellClass += "hover:bg-gray-500 transform hover:scale-105"; // Highlight potential target cells
    } else {
      cellClass += "bg-gray-700 hover:bg-gray-600 transform hover:scale-105"; // Default cell appearance
    }

    const handleClick = () => {
      if (onCellClick) {
        // Allow clicking on opponent grid for attack abilities
        if (!isPlayerGrid && !cell.hit && !cell.miss) {
          setLastAttackedCell({ x, y });
          onCellClick(x, y, false, cellLabel);
        }
      }
    };

    const handleMouseEnter = () => {
      if (!isPlayerGrid && activeAbility) {
        setHoverCoords({ x, y });
      }
    };

    const handleMouseLeave = () => {
      if (hoverCoords?.x === x && hoverCoords?.y === y) {
        setHoverCoords(null);
      }
    };

    return (
      <div
        className={cellClass}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{
          cursor: (!isPlayerGrid && onCellClick && !cell.hit && !cell.miss)
            ? 'pointer' : 'default',
          transition: 'all 0.2s ease-in-out'
        }}
        title={cellLabel}
      >
        {!cell.hit && !cell.miss && !isPlayerGrid && (
          <span className="text-[16px] text-white/70 group-hover:text-white font-mono font-semibold absolute">
            {cellLabel}
          </span>
        )}
      </div>
    );
  };

  // Check if JAM is active on this player's grid
  const jamActive = isPlayerGrid && playerData?.abilities?.JAM?.installed && !playerData?.abilities?.JAM?.used;

  return (
    <div className={`w-full h-full ${jamActive ? 'jam-shield-active' : ''}`}>
      <div className="w-full h-full grid gap-2" style={{ gridTemplateColumns: `repeat(${gridSize}, 1fr)`, gridTemplateRows: `repeat(${gridSize}, 1fr)` }}>
        {normalizedGrid.map((row, y) =>
          row.map((cell, x) => (
            <React.Fragment key={`${y}-${x}`}>
              {renderCell(cell, x, y)}
            </React.Fragment>
          ))
        )}
      </div>

      {activeAbility && (
        <div className="mt-2 text-center text-xs text-white animate-pulse">
          Using <span className="font-bold">{activeAbility}</span> ability
        </div>
      )}
    </div>
  );
};

export default GameBoard;